import {
  BillingsCargoTaxiContributionReal,
  BillingsCargoTaxiContributionDiscountType,
} from "../../common/contributions/discount/types/billings-cargo-taxi-contributions-discount";
import BillingFormData, {
  BillingFormDataContributions,
} from "../types/billing-form.data";
import BillingsOrderCargoTaxiCreateParams, {
  BillingsOrderCargoTaxiCreateParamsBillingNode,
  BillingsOrderCargoTaxiCreateParamsContribution,
  BillingsOrderCargoTaxiCreateParamsContributionType,
} from "../../../../../common/services/billings/order/cargo-taxi/create/billings-order-cargo-taxi-create-params";
import BillingsNode from "../../../common/types/billings-node";

const createBillingNode = (
  node: BillingsNode,
): BillingsOrderCargoTaxiCreateParamsBillingNode => {
  return {
    uuid: node.id,
    nodeUuid: node.nodeId,
    orderUuid: node.orderId ?? undefined,
    lat: node.lat,
    lon: node.lon,
    distance: node.distance,
    description: node.description,
    checkoutDate: node.checkoutDate ?? undefined,
    checkoutEventUuid: node.checkoutEventUuid ?? undefined,
    plannedDate: node.plannedDate ?? undefined,
    haltingTime: node.haltingTime,
    allowCharge: node.isHighwayAllowed,
    highwayCharge: node.highwayCharge,
    plannedDistance: node.plannedDistance,
    editableCoordinates: node.isEditable,
  };
};

const createBillingNodes = (
  nodes: BillingsNode[],
): BillingsOrderCargoTaxiCreateParamsBillingNode[] => {
  return nodes.map(createBillingNode);
};

const createSaveContributionType = (
  contributionType:
    | BillingsCargoTaxiContributionDiscountType.DISCOUNT_BEING_LATE
    | BillingsCargoTaxiContributionDiscountType.DISCOUNT_INCOMPATIBLE_CAR
    | BillingsCargoTaxiContributionDiscountType.DISCOUNT_OPTIMIZATION
    | BillingsCargoTaxiContributionDiscountType.DISCOUNT_OTHER,
):
  | BillingsOrderCargoTaxiCreateParamsContributionType.DISCOUNT_BEING_LATE
  | BillingsOrderCargoTaxiCreateParamsContributionType.DISCOUNT_INCOMPATIBLE_CAR
  | BillingsOrderCargoTaxiCreateParamsContributionType.DISCOUNT_OPTIMIZATION
  | BillingsOrderCargoTaxiCreateParamsContributionType.DISCOUNT_OTHER => {
  switch (contributionType) {
    case BillingsCargoTaxiContributionDiscountType.DISCOUNT_BEING_LATE:
      return BillingsOrderCargoTaxiCreateParamsContributionType.DISCOUNT_BEING_LATE;
    case BillingsCargoTaxiContributionDiscountType.DISCOUNT_INCOMPATIBLE_CAR:
      return BillingsOrderCargoTaxiCreateParamsContributionType.DISCOUNT_INCOMPATIBLE_CAR;
    case BillingsCargoTaxiContributionDiscountType.DISCOUNT_OPTIMIZATION:
      return BillingsOrderCargoTaxiCreateParamsContributionType.DISCOUNT_OPTIMIZATION;
    case BillingsCargoTaxiContributionDiscountType.DISCOUNT_OTHER:
      return BillingsOrderCargoTaxiCreateParamsContributionType.DISCOUNT_OTHER;
  }
};

const createBillingDiscountContribution = (
  contribution: BillingsCargoTaxiContributionReal,
): BillingsOrderCargoTaxiCreateParamsContribution => {
  switch (contribution.type) {
    case BillingsCargoTaxiContributionDiscountType.CONTRACT_CORRECTION_PERCENTAGE:
      return {
        type: BillingsOrderCargoTaxiCreateParamsContributionType.CONTRACT_CORRECTION_PERCENTAGE,
        percentage: contribution.percentage ?? 0,
        amount: contribution.amount ?? 0,
      };
    case BillingsCargoTaxiContributionDiscountType.DISCOUNT_BEING_LATE:
    case BillingsCargoTaxiContributionDiscountType.DISCOUNT_INCOMPATIBLE_CAR:
    case BillingsCargoTaxiContributionDiscountType.DISCOUNT_OPTIMIZATION:
    case BillingsCargoTaxiContributionDiscountType.DISCOUNT_OTHER:
      return {
        type: createSaveContributionType(contribution.type),
        comment: contribution.comment,
        amount: contribution.amount ?? 0,
      };
  }
};

const createBillingContributions = (
  contributions: BillingFormDataContributions,
): BillingsOrderCargoTaxiCreateParamsContribution[] => {
  const discountContributions = contributions.discount.filter(
    (contribution) => {
      contribution.type !== null;
    },
  ) as BillingsCargoTaxiContributionReal[];

  return [
    ...discountContributions.map((contribution) =>
      createBillingDiscountContribution(contribution),
    ),
  ];
};

const createSaveBillingRequest = (
  orderUuid: string,
  data: BillingFormData,
): BillingsOrderCargoTaxiCreateParams => {
  const result: BillingsOrderCargoTaxiCreateParams = {
    orderUuid: orderUuid,
    billingNodes: createBillingNodes(data.billingNodes),
    billingContributions: createBillingContributions(data.billingContributions),
  };
  return result;
};

const billingSaveRequestFactory = {
  createSaveBillingRequest,
};

export default billingSaveRequestFactory;
